import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  ButtonBase,
} from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useAuthState } from 'react-firebase-hooks/auth';
import LogFormShell from '../UI/LogFormShell';
import LogDateField from '../UI/LogDateField';
import LogTimeField from '../UI/LogTimeField';
import LogDatePickerPopover from '../UI/LogDatePickerPopover';
import SimpleNotesInput from '../UI/SimpleNotesInput';
import useChildName from '../../hooks/useChildName';
import { auth } from '../../services/firebase';
import { saveFoodLogEntry } from '../../services/foodLogService';
import { getCalendarDateKey } from '../../utils/calendarDateKey';
import { getAdaptiveFoodLogConfig, resolveAdaptiveFoodLogPresentation } from './shared/adaptiveFoodLogConfig';

const pad = (n) => String(n).padStart(2, '0');

const FOOD = {
  bg: '#F6F8F6',
  ink: '#1A2320',
  sage: '#6FA594',
};

const getDefaultDate = () => getCalendarDateKey(new Date());

const getDefaultTime = () => {
  const now = new Date();
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

const getDefaultNotes = () => ({ text: '', mediaFile: null, audioBlob: null });

const getFoodEditPrefillText = (initialData = {}) => {
  const foodDetails = initialData.foodDetails || {};
  return foodDetails.whatWasEaten || '';
};

const FoodLogSheet = ({ open, onClose, child, mode = 'create', initialData = null, entryId = null }) => {
  const [user] = useAuthState(auth);
  const { childName } = useChildName(child?.id);
  const [foodDate, setFoodDate] = useState(getDefaultDate);
  const [foodTime, setFoodTime] = useState(getDefaultTime);
  const [mealType, setMealType] = useState(null);
  const [whatWasEatenData, setWhatWasEatenData] = useState(getDefaultNotes);
  const [whatWasEatenClearToken, setWhatWasEatenClearToken] = useState(0);
  const [portion, setPortion] = useState(null);
  const [reaction, setReaction] = useState('None');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const foodConfig = useMemo(() => getAdaptiveFoodLogConfig(child), [child]);
  const foodPresentation = useMemo(() => resolveAdaptiveFoodLogPresentation({ child }), [child]);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initialData) {
      const foodDetails = initialData.foodDetails || {};
      const timestamp = initialData.timestamp?.toDate ? initialData.timestamp.toDate() : (initialData.timestamp ? new Date(initialData.timestamp) : null);
      const nextDate = foodDetails.localDate || initialData.localDate || (timestamp instanceof Date && !Number.isNaN(timestamp.getTime())
        ? [
            timestamp.getFullYear(),
            String(timestamp.getMonth() + 1).padStart(2, '0'),
            String(timestamp.getDate()).padStart(2, '0'),
          ].join('-')
        : getDefaultDate());
      const nextTime = foodDetails.localTime || initialData.localTime || (timestamp instanceof Date && !Number.isNaN(timestamp.getTime())
        ? timestamp.toTimeString().slice(0, 5)
        : getDefaultTime());

      setFoodDate(nextDate);
      setFoodTime(nextTime);
      setMealType(
        foodDetails.mealType || null
      );
      setWhatWasEatenData({
        text: getFoodEditPrefillText(initialData),
        mediaFile: null,
        audioBlob: null,
      });
      setPortion(foodDetails.portion || null);
      setReaction(foodDetails.reaction || 'None');
      setNotes(foodDetails.notes || '');
      return;
    }

    setFoodDate(getDefaultDate());
    setFoodTime(getDefaultTime());
    setMealType(null);
    setWhatWasEatenData(getDefaultNotes());
    setWhatWasEatenClearToken((token) => token + 1);
    setPortion(null);
    setReaction('None');
    setNotes('');
  }, [open, child?.id, initialData, mode]);

  const resolvedChildName = childName || child?.name || 'Child';
  const foodTimestamp = useMemo(() => {
    const timestamp = new Date(`${foodDate}T${foodTime}`);
    return Number.isNaN(timestamp.getTime()) ? new Date() : timestamp;
  }, [foodDate, foodTime]);

  const foodDateObject = useMemo(() => {
    const parsed = new Date(`${foodDate}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }, [foodDate]);

  const formatDisplayDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const handleDatePickerOpen = (event) => {
    setDatePickerAnchor(event.currentTarget);
  };

  const handleDatePickerClose = () => {
    setDatePickerAnchor(null);
  };

  const handleDateChange = (nextDate) => {
    if (!nextDate || Number.isNaN(nextDate.getTime())) {
      return;
    }

    setFoodDate([
      nextDate.getFullYear(),
      String(nextDate.getMonth() + 1).padStart(2, '0'),
      String(nextDate.getDate()).padStart(2, '0'),
    ].join('-'));
    handleDatePickerClose();
  };

  const handleSave = async () => {
    if (!child?.id || !user?.uid) return;

    setSaving(true);
    try {
      const docRef = await saveFoodLogEntry({
        child, user, foodConfig, foodPresentation, mode, entryId, initialData,
        foodLog: { foodDate, foodTime, foodTimestamp, mealType, whatWasEatenData, portion, reaction, notes },
      });

      window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
        detail: {
          id: docRef.id,
          childId: child.id,
          collection: 'dailyLogs',
        },
      }));

      window.dispatchEvent(new CustomEvent('captureez:timeline-focus-date', {
        detail: {
          childId: child.id,
          date: foodTimestamp.toISOString(),
        },
      }));

      setWhatWasEatenClearToken((token) => token + 1);
      onClose?.();
    } catch (error) {
      console.error('Error saving food log:', error);
    } finally {
      setSaving(false);
    }
  };

  const renderPillRow = (options, value, onChange, { activeColor = FOOD.sage, selectedOverride = {} } = {}) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {options.map((option) => {
        const optionValue = option.value || option;
        const selected = value === optionValue;
        const style = selectedOverride[optionValue] || option || {};

        return (
          <ButtonBase
            key={optionValue}
            component="button"
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(optionValue)}
            sx={{
              px: 1.45,
              py: 0.8,
              borderRadius: 9999,
              border: '1.5px solid',
              borderColor: selected ? (style.border || activeColor) : '#D9E1DB',
              bgcolor: selected ? (style.bg || '#EAF2EE') : '#FFFFFF',
              color: selected ? (style.ink || FOOD.ink) : FOOD.ink,
              fontSize: 12.5,
              fontWeight: selected ? 700 : 600,
              lineHeight: 1,
              boxShadow: selected ? `inset 0 0 0 1px ${style.border || activeColor}` : 'none',
              transition: 'transform 140ms ease, box-shadow 180ms ease, background-color 180ms ease, border-color 180ms ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                bgcolor: selected ? (style.bg || '#EAF2EE') : '#F8FAF8',
              },
              '&:focus-visible': {
                outline: `2px solid ${style.border || activeColor}`,
                outlineOffset: 2,
              },
            }}
          >
            {option.label || optionValue}
          </ButtonBase>
        );
      })}
    </Box>
  );

  const renderMealGrid = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 1,
      }}
    >
      {foodConfig.mealTypes.map((option) => {
        const selected = mealType === option.label;
        return (
          <ButtonBase
            key={option.label}
            component="button"
            type="button"
            aria-pressed={selected}
            onClick={() => setMealType(option.label)}
            sx={{
              minHeight: 116,
              px: 1.3,
              py: 1.5,
              borderRadius: '14px',
              border: '1.5px solid',
              borderColor: selected ? option.border : '#DCE6DE',
              bgcolor: selected ? option.bg : '#FFFFFF',
              color: option.ink,
              boxShadow: selected ? `inset 0 0 0 1px ${option.border}` : 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              transition: 'transform 140ms ease, box-shadow 180ms ease, background-color 180ms ease, border-color 180ms ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                bgcolor: selected ? option.bg : '#FAFCFA',
              },
              '&:focus-visible': {
                outline: `2px solid ${option.border}`,
                outlineOffset: 2,
              },
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '14px',
                bgcolor: selected ? '#FFFFFF' : option.iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
                lineHeight: 1,
              }}
            >
              {option.iconComponent ? <option.iconComponent sx={{ fontSize: 24, color: option.ink }} /> : null}
            </Box>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: selected ? 700 : 600,
                lineHeight: 1.15,
                textAlign: 'center',
                color: selected ? option.ink : FOOD.ink,
              }}
            >
              {option.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );

  const formBody = (
    <Stack spacing={2.2} sx={{ bgcolor: FOOD.bg, borderRadius: 4, px: { xs: 0, sm: 0 }, pb: 0.25 }}>
      <Box sx={{ position: 'relative' }}>
        <Stack
          direction="row"
          spacing={1.25}
          sx={{ width: '100%', alignItems: 'stretch' }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <LogDateField
              label="Date"
              value={formatDisplayDate(foodDateObject)}
              onClick={handleDatePickerOpen}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <LogTimeField
              label="Time"
              value={foodTime}
              onChange={(e) => setFoodTime(e.target.value)}
            />
          </Box>
        </Stack>

        <LogDatePickerPopover
          open={Boolean(datePickerAnchor)}
          anchorEl={datePickerAnchor}
          onClose={handleDatePickerClose}
          value={foodDateObject}
          onChange={handleDateChange}
        />
      </Box>

      <Box>
        <Typography
          sx={{
            mb: 1,
            fontSize: '1.2rem',
            lineHeight: 1.05,
            fontWeight: 700,
            color: FOOD.ink,
          }}
        >
          {foodConfig.typeSectionLabel}
        </Typography>
        {renderMealGrid()}
      </Box>

      <Box>
        <Typography
          sx={{
            mb: 1,
            fontSize: '1.15rem',
            lineHeight: 1.08,
            fontWeight: 700,
            color: FOOD.ink,
          }}
        >
          {foodConfig.eatenSectionLabel}
        </Typography>
        <SimpleNotesInput
          value={whatWasEatenData}
          onDataChange={setWhatWasEatenData}
          clearData={whatWasEatenClearToken}
          placeholder={foodConfig.eatenPlaceholder}
          hintText="Tip: use your phone mic to dictate notes."
          textFieldSx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '18px',
            },
          }}
        />
      </Box>

      <Box>
        <Typography
          sx={{
            mb: 1,
            fontSize: '1.15rem',
            lineHeight: 1.08,
            fontWeight: 700,
            color: FOOD.ink,
          }}
        >
          {foodConfig.amountSectionLabel}
        </Typography>
        {renderPillRow(foodConfig.portionOptions, portion, setPortion, {
          activeColor: FOOD.sage,
          selectedOverride: {
            Refused: {
              bg: '#FEF2F2',
              border: '#F9BABA',
              ink: '#B45050',
            },
          },
        })}
      </Box>

      <Box>
        <Typography
          sx={{
            mb: 1,
            fontSize: '1.15rem',
            lineHeight: 1.08,
            fontWeight: 700,
            color: FOOD.ink,
          }}
        >
          {foodConfig.reactionSectionLabel}
        </Typography>
        {renderPillRow(foodConfig.reactionOptions, reaction, setReaction, {
          activeColor: FOOD.sage,
          selectedOverride: {
            None: { bg: '#F0F0F0', border: '#D9DED9', ink: '#5F6B66' },
            'Spit out': { bg: '#F6DED2', border: '#E3B69F', ink: '#7A4A2F' },
            Gagged: { bg: '#EAE8F4', border: '#C6BFE2', ink: '#514A73' },
            Refused: { bg: '#FEF2F2', border: '#F9BABA', ink: '#B45050' },
            'Allergy concern': { bg: '#FEF2F2', border: '#F9BABA', ink: '#B45050' },
            'Upset stomach': { bg: '#E8F0F8', border: '#BED2EA', ink: '#385A78' },
          },
        })}
        {reaction === 'Allergy concern' ? (
          <Box
            sx={{
              mt: 1,
              px: 1.5,
              py: 1.05,
              borderRadius: '18px',
              bgcolor: '#FEF2F2',
              border: '1px solid #F9BABA',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#B45050',
                mt: 0.05,
              }}
            >
              <WarningAmberOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 15, lineHeight: 1.12, color: '#B45050', fontWeight: 700 }}>
                {foodConfig.allergyWarningTitle}
              </Typography>
              <Typography sx={{ fontSize: 13.5, lineHeight: 1.25, color: '#B45050', fontWeight: 500, mt: 0.25 }}>
                {foodConfig.allergyWarningBody}
              </Typography>
            </Box>
          </Box>
        ) : null}
      </Box>

      <SimpleNotesInput
        label={foodConfig.notesLabel}
        hintText="Tip: use your phone mic to dictate notes."
        value={notes}
        onDataChange={(data) => setNotes(data?.text || '')}
        placeholder={foodConfig.notesPlaceholder}
        minRows={2}
      />
    </Stack>
  );

  const footerActions = (
    <>
      <Divider sx={{ my: 3 }} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !child?.id || !user?.uid || !mealType}
          sx={{
            width: '100%',
            height: 54,
            borderRadius: 9999,
            bgcolor: mealType ? FOOD.sage : '#E5ECE8',
            color: mealType ? '#FFFFFF' : '#A4B0A9',
            fontWeight: 700,
            boxShadow: mealType ? '0 4px 14px rgba(111,165,148,0.3)' : 'none',
            '&:hover': {
              bgcolor: mealType ? FOOD.sage : '#E5ECE8',
              boxShadow: mealType ? '0 4px 14px rgba(111,165,148,0.3)' : 'none',
            },
            '&:disabled': {
              bgcolor: '#E5ECE8',
              color: '#A4B0A9',
            },
          }}
        >
          {saving ? foodConfig.savingLabel : foodConfig.saveLabel}
        </Button>
      </Box>
    </>
  );

  return (
    <LogFormShell
      open={open}
      onClose={onClose}
      title={foodConfig.title}
      titleBadge={resolvedChildName}
      compactTitle
      footer={footerActions}
      mobileBreakpoint="md"
      maxWidth="sm"
    >
      {formBody}
    </LogFormShell>
  );
};

export default FoodLogSheet;
